import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators } from 'redux';

import { CertifyUserTicketMutation } from 'types/graphql-api.generated';

import {
  TrackingEventNames,
  TrackingContextObject
} from 'common/constants/trackingEventsNames';
import eventEmitter from 'common/services/events/eventEmitter';

import { certifyTicketSuccess } from 'website/actions/OpinionActions';
import allocineContainer from 'website/containers/allocineContainer';
import { State, Dispatch } from 'website/reducers';

type CertifyTicketEventPayload = {
  response: NonNullable<
    CertifyUserTicketMutation['certifyUserTicket']['userEntityLeaf']
  >;
  context?: TrackingContextObject;
};

/**
 * mapStateToProps - Extracts state data needed for the handler
 */
const mapStateToProps = (state: State) => ({
  data: state.data
});

/**
 * mapDispatchToProps - Binds action creators to dispatch
 */
const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      certifyTicketSuccess
    },
    dispatch
  );

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

/**
 * Standalone component that listens to CERTIFY_TICKET_EVENT and dispatches Redux action
 */
const StandaloneCertifyUserTicket = ({
  data,
  certifyTicketSuccess: dispatchCertifyTicketSuccess
}: PropsFromRedux) => {
  const dataRef = useRef(data);
  const dispatchRef = useRef(dispatchCertifyTicketSuccess);

  useEffect(() => {
    dataRef.current = data;
    dispatchRef.current = dispatchCertifyTicketSuccess;
  }, [data, dispatchCertifyTicketSuccess]);

  useEffect(() => {
    const handleCertifyTicketEvent = (payload: CertifyTicketEventPayload) => {
      const { response: userEntityLeaf } = payload;

      if (userEntityLeaf) {
        const entityId = userEntityLeaf.entity?.id;

        if (entityId) {
          const entity = dataRef.current.all[entityId];

          if (entity) {
            dispatchRef.current(userEntityLeaf, entity);
          }
        }
      }
    };

    eventEmitter.on(
      TrackingEventNames.CERTIFY_TICKET_EVENT,
      handleCertifyTicketEvent
    );

    return () => {
      eventEmitter.off(
        TrackingEventNames.CERTIFY_TICKET_EVENT,
        handleCertifyTicketEvent
      );
    };
  }, []);

  return null;
};

const ConnectedStandaloneCertifyUserTicket = connector(
  StandaloneCertifyUserTicket
);

/**
 * Mounts the StandaloneCertifyUserTicket component globally
 */
export default function standaloneCertifyUserTicket() {
  const container = document.createElement('div');
  container.style.display = 'none';
  document.body.appendChild(container);

  const WrappedComponent = allocineContainer(
    ConnectedStandaloneCertifyUserTicket
  );
  const root = createRoot(container);
  root.render(<WrappedComponent />);
}
